import type {
  AssetStoryblok,
  MediaElementStoryblok,
} from "@/generated/sb/sb-types";

import isImageOrVideo from "@/lib/utils/isImageOrVideo";

import {
  getStoryblokAlt,
  getStoryblokFile,
  getStoryblokFocus,
  getStoryblokHeight,
  getStoryblokWidth,
} from "./mediaFormatters";

const getMediaType = (media: AssetStoryblok) => {
  return isImageOrVideo(media.filename ?? "");
};

const FormatStoryblokMedia = (blok: MediaElementStoryblok | undefined) => {
  if (!blok) return { desktop: undefined, mobile: undefined };
  const { desktopMedia, mobileMedia, _uid } = blok;
  if (!desktopMedia?.filename && !mobileMedia?.filename)
    return { desktop: undefined, mobile: undefined };

  const formattedDesktopMedia = {
    ...desktopMedia,
    alt: desktopMedia?.alt ?? null,
    copyright: desktopMedia?.copyright ?? undefined,
    id: desktopMedia?.id ?? 0,
    title: desktopMedia?.title ?? null,
    focus: desktopMedia?.focus ?? null,
  };

  const formattedMobileMedia = {
    ...mobileMedia,
    alt: mobileMedia?.alt ?? null,
    copyright: mobileMedia?.copyright ?? undefined,
    id: mobileMedia?.id ?? 0,
    title: mobileMedia?.title ?? null,
    focus: mobileMedia?.focus ?? null,
    filename: mobileMedia?.filename ?? "",
    name: mobileMedia?.name ?? "",
    fieldtype: "asset" as const,
  };

  const desktop = {
    file: getStoryblokFile(formattedDesktopMedia) ?? undefined,
    alt: getStoryblokAlt(formattedDesktopMedia),
    width: getStoryblokWidth(formattedDesktopMedia),
    height: getStoryblokHeight(formattedDesktopMedia),
    focus: getStoryblokFocus(formattedDesktopMedia),
    _uid,
    ...getMediaType(formattedDesktopMedia),
  };
  const mobile = formattedMobileMedia
    ? {
        file: getStoryblokFile(formattedMobileMedia) ?? undefined,
        alt: getStoryblokAlt(formattedMobileMedia),
        _uid,
        width: getStoryblokWidth(formattedMobileMedia),
        height: getStoryblokHeight(formattedMobileMedia),
        focus: getStoryblokFocus(formattedMobileMedia),
        ...getMediaType(formattedMobileMedia),
      }
    : undefined;

  return { desktop, mobile };
};

export type FormattedMedia = ReturnType<typeof FormatStoryblokMedia>;

export default FormatStoryblokMedia;
